import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
	ServiceCategory,
	ServiceCategoryRequest,
	AssignServicesRequest,
} from '../types/serviceCategory';

/**
 * Service categories for a provider location. Provider-only: the staff portal
 * reads a service's category name off the service itself and never manages the
 * categories.
 */
export const serviceCategoryApi = {
	/** Categories at a location, alphabetical, each with its service count. */
	list: async (locationId: number): Promise<ServiceCategory[]> =>
		apiClient.get<ServiceCategory[]>(
			`${API_ENDPOINTS.SERVICE_CATEGORY.BASE}?locationId=${locationId}`
		),

	/** Names must be unique at the location, ignoring case — 409 otherwise. */
	create: async (locationId: number, data: ServiceCategoryRequest): Promise<ServiceCategory> =>
		apiClient.post<ServiceCategory>(
			`${API_ENDPOINTS.SERVICE_CATEGORY.BASE}?locationId=${locationId}`,
			data
		),

	/** The new name is written onto every service filed under the category. */
	rename: async (
		categoryId: number,
		locationId: number,
		data: ServiceCategoryRequest
	): Promise<ServiceCategory> =>
		apiClient.put<ServiceCategory>(
			`${API_ENDPOINTS.SERVICE_CATEGORY.DETAIL(categoryId)}?locationId=${locationId}`,
			data
		),

	/** Services filed under the category are kept, and left without a category. */
	delete: async (categoryId: number, locationId: number): Promise<void> => {
		await apiClient.delete<void>(
			`${API_ENDPOINTS.SERVICE_CATEGORY.DETAIL(categoryId)}?locationId=${locationId}`
		);
	},

	/**
	 * File several services under one category in a single call. Each service
	 * moves out of whatever category it was in; every id must belong to this
	 * location. There is no bulk counterpart for clearing a category — a single
	 * service is unassigned by saving it with no category from the edit modal.
	 */
	assignServices: async (
		categoryId: number,
		locationId: number,
		data: AssignServicesRequest
	): Promise<ServiceCategory> =>
		apiClient.put<ServiceCategory>(
			`${API_ENDPOINTS.SERVICE_CATEGORY.SERVICES(categoryId)}?locationId=${locationId}`,
			data
		),
};

export default serviceCategoryApi;
